import {
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Pill,
  Plane,
  Shield,
  Trophy,
} from "lucide-react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";

type StringKey = {
  [K in keyof Translation]: Translation[K] extends string ? K : never;
}[keyof Translation];

interface DemoStepDefinition {
  id: number;
  key: string;
  titleKey: StringKey;
  descriptionKey: StringKey;
  path: string;
  icon: React.ElementType;
  color: string;
}

export interface DemoStep extends DemoStepDefinition {
  title: string;
  description: string;
}

export const DEMO_STEPS: DemoStepDefinition[] = [
  {
    id: 1,
    key: "travel",
    titleKey: "demoStepTravelTitle",
    descriptionKey: "demoStepTravelDescription",
    path: "/traveler/travels",
    icon: Plane,
    color: "bg-blue-600",
  },
  {
    id: 2,
    key: "health-data",
    titleKey: "demoStepHealthDataTitle",
    descriptionKey: "demoStepHealthDataDescription",
    path: "/traveler/medications",
    icon: Pill,
    color: "bg-purple-600",
  },
  {
    id: 3,
    key: "checkin",
    titleKey: "demoStepCheckinTitle",
    descriptionKey: "demoStepCheckinDescription",
    path: "/traveler/health/checkin",
    icon: CheckSquare,
    color: "bg-green-600",
  },
  {
    id: 4,
    key: "emergency",
    titleKey: "demoStepEmergencyTitle",
    descriptionKey: "demoStepEmergencyDescription",
    path: "/traveler/card",
    icon: Shield,
    color: "bg-red-600",
  },
  {
    id: 5,
    key: "card",
    titleKey: "demoStepCardTitle",
    descriptionKey: "demoStepCardDescription",
    path: "/traveler/health/appointment",
    icon: CreditCard,
    color: "bg-orange-600",
  },
];

/** The demo steps with their title and description in the app language. */
export function useDemoSteps(): DemoStep[] {
  const { t } = useI18nStore();
  return DEMO_STEPS.map((step) => ({
    ...step,
    title: t[step.titleKey],
    description: t[step.descriptionKey],
  }));
}

interface DemoNavigatorProps {
  currentStep?: number;
}

export const DemoNavigator: React.FC<DemoNavigatorProps> = ({
  currentStep,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18nStore();
  const steps = useDemoSteps();

  // Determine active step by path if not provided
  const activeStep =
    currentStep ??
    DEMO_STEPS.find((s) => location.pathname.startsWith(s.path))?.id ??
    0;

  const isDemoMode = sessionStorage.getItem("demo-mode") === "true";
  if (!isDemoMode) return null;

  const current = steps.find((s) => s.id === activeStep);
  const next = steps.find((s) => s.id === (activeStep ?? 0) + 1);
  const prev = steps.find((s) => s.id === (activeStep ?? 0) - 1);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 text-white shadow-2xl border-t border-gray-700">
      <div className="max-w-5xl mx-auto px-4 py-3">
        {/* Progress Bar */}
        <div className="flex items-center gap-2 mb-2">
          {steps.map((step) => {
            const isActive = step.id === activeStep;
            const isDone = (activeStep ?? 0) > step.id;
            const StepIcon = step.icon;
            return (
              <button
                key={step.id}
                onClick={() => navigate(step.path)}
                title={step.title}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  isActive
                    ? `${step.color} text-white scale-105`
                    : isDone
                      ? "bg-gray-600 text-gray-300"
                      : "bg-gray-800 text-gray-500 hover:bg-gray-700"
                }`}
              >
                <StepIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{step.title}</span>
                <span className="sm:hidden">{step.id}</span>
              </button>
            );
          })}
          <button
            onClick={() => {
              sessionStorage.removeItem("demo-mode");
              navigate("/dashboard");
            }}
            className="ml-auto text-xs text-gray-500 hover:text-gray-300 underline"
          >
            {t.demoExit}
          </button>
        </div>

        {/* Navigation Row */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => prev && navigate(prev.path)}
            disabled={!prev}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            {prev ? prev.title : t.demoStart}
          </button>

          <div className="text-center">
            {current && (
              <div className="text-sm font-semibold">
                {t.demoStepOf
                  .replace("{current}", String(activeStep))
                  .replace("{total}", String(steps.length))
                  .replace("{title}", current.title)}
              </div>
            )}
            <div className="text-xs text-gray-400">{current?.description}</div>
          </div>

          <button
            onClick={() => {
              if (next) {
                navigate(next.path);
              } else {
                navigate("/demo/complete");
              }
            }}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-500 transition-colors"
          >
            {next ? next.title : t.demoFinish}
            {next ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <Trophy className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoNavigator;
